import React from 'react';
import { Chip } from '@mui/material';

/** unset | none | valid | expiring | expired */
export const getSubMatExpiryDaysLeft = (file) => {
  if (!file?.endDate || file.validYN === true) return null;
  const end = new Date(file.endDate);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDay = new Date(end);
  endDay.setHours(0, 0, 0, 0);
  return Math.round((endDay - today) / (24 * 60 * 60 * 1000));
};

export const getSubMatExpiryStatus = (file) => {
  if (!file) return 'unset';
  if (file.validYN === true) return 'none';
  if (!file.endDate) return 'unset';
  const diffDays = getSubMatExpiryDaysLeft(file);
  if (diffDays == null) return 'unset';
  if (diffDays < 0) return 'expired';
  if (diffDays <= 7) return 'expiring';
  return 'valid';
};

export const SUB_MAT_EXPIRY_STATUS_LABEL = {
  unset: '미설정',
  none: '만료일 없음',
  valid: '유효',
  expiring: '임박',
  expired: '만료',
};

export const SUB_MAT_EXPIRY_STATUS_COLOR = {
  unset: 'default',
  none: 'info',
  valid: 'success',
  expiring: 'warning',
  expired: 'error',
};

/** 레거시(자재승인 표·서류 Chip) 만료 글자색 */
export const SUB_MAT_EXPIRED_TEXT_COLOR = '#DB4E2E';

/** 상태 컬럼용 글자색. 만료만 강조 */
export const getSubMatExpiryStatusTextColor = (file) => {
  if (getSubMatExpiryStatus(file) === 'expired') return SUB_MAT_EXPIRED_TEXT_COLOR;
  return undefined;
};

/** 만료일 컬럼: 날짜만 (상태 문구는 상태 컬럼). 만료일 없음 → 없음 */
export const formatSubMatEndDate = (file) => {
  if (!file || file.fileRole === 'TEST_COMPARISON') return '';
  if (file.validYN === true) return '없음';
  if (!file.endDate) return '';
  const d = new Date(file.endDate);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * 상태 컬럼 텍스트.
 * 임박(0~7일): -7 … -1, 당일 0 카운트다운. 그 외는 고정 라벨.
 */
export const formatSubMatExpiryStatusText = (file) => {
  if (!file || file.fileRole === 'TEST_COMPARISON') return '';
  const status = getSubMatExpiryStatus(file);
  if (status === 'expiring') {
    const daysLeft = getSubMatExpiryDaysLeft(file);
    if (daysLeft == null) return SUB_MAT_EXPIRY_STATUS_LABEL.expiring;
    return `-${daysLeft}`;
  }
  return SUB_MAT_EXPIRY_STATUS_LABEL[status] || '';
};

/** 정렬: 미설정 먼저, 만료일 없음은 맨 뒤 */
export const getSubMatExpirySortValue = (file) => {
  if (!file || file.fileRole === 'TEST_COMPARISON') return Number.NEGATIVE_INFINITY;
  if (file.validYN === true) return Number.POSITIVE_INFINITY;
  if (!file.endDate) return Number.NEGATIVE_INFINITY;
  const t = new Date(file.endDate).getTime();
  return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t;
};

/** 상태 정렬 키 (미설정 → 만료 → 임박 → 유효 → 만료일 없음) */
export const getSubMatExpiryStatusSortValue = (file) => {
  const order = { unset: 0, expired: 1, expiring: 2, valid: 3, none: 4 };
  if (!file || file.fileRole === 'TEST_COMPARISON') return -1;
  return order[getSubMatExpiryStatus(file)] ?? 0;
};

/** 다이얼로그·점검 화면용 Chip (목록 컬럼은 텍스트 사용) */
export const SubMatExpiryStatusChip = ({ file, size = 'small', sx }) => {
  const status = getSubMatExpiryStatus(file);
  const label = formatSubMatExpiryStatusText(file) || status;
  return (
    <Chip
      size={size}
      label={label}
      color={SUB_MAT_EXPIRY_STATUS_COLOR[status] || 'default'}
      variant={status === 'unset' ? 'outlined' : 'filled'}
      sx={sx}
    />
  );
};
